import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, TrendingUp, FolderKanban, LogOut, ArrowRight, Sparkles, Flame, Rocket, Crown, Play, Award, Download, Package, Music, KeyRound, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import AIChat from "../components/AIChat";
import api, { BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Progress } from "../components/ui/progress";

const BACKEND = BACKEND_URL;

const STAGE_META = {
    aspiring: { icon: Sparkles, label: "Aspiring Artist", step: 1 },
    emerging: { icon: Flame, label: "Emerging Artist", step: 2 },
    independent: { icon: Rocket, label: "Independent Artist", step: 3 },
    professional: { icon: Crown, label: "Professional Artist", step: 4 },
};

export default function Dashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [pw, setPw] = useState("");
    const [pwBusy, setPwBusy] = useState(false);
    const [pwDone, setPwDone] = useState(false);
    useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

    const savePassword = async () => {
        if (pw.length < 6) { toast.error("Password must be at least 6 characters."); return; }
        setPwBusy(true);
        try { await api.post("/auth/set-password", { password: pw }); setPwDone(true); setPw(""); toast.success("Password set — you can now log in with your email."); }
        catch (e) { toast.error(e?.response?.data?.detail || "Could not set password."); }
        finally { setPwBusy(false); }
    };

    const stage = STAGE_META[data?.stats?.stage] || STAGE_META.aspiring;
    const StageIcon = stage.icon;

    const downloadProduct = async (slug, title) => {
        try {
            const res = await api.get(`/products/${slug}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = title || slug; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not download this file."); }
    };

    return (
        <div className="min-h-screen bg-cream" data-testid="dashboard-page">
            <header className="border-b border-[#e8e1d4] bg-white/90 backdrop-blur-xl sticky top-0 z-40">
                <div className="mx-container flex items-center justify-between h-16">
                    <Logo size="sm" withTagline={false} />
                    <div className="flex items-center gap-4">
                        <Link to="/" className="text-sm text-neutral-500 hover:text-ink hidden sm:block">View site</Link>
                        <button onClick={async () => { await logout(); navigate("/"); }} className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-brand-golddark" data-testid="dashboard-logout"><LogOut className="h-4 w-4" /> Log out</button>
                    </div>
                </div>
            </header>

            <div className="mx-container py-10 lg:py-14">
                <p className="overline mb-3">Artist Command Center</p>
                <h1 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl">Welcome, <span className="italic text-brand-gold">{user?.name?.split(" ")[0] || "Artist"}.</span></h1>

                <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon={BookOpen} label="Enrolled courses" value={data?.stats?.courses ?? 0} />
                    <StatCard icon={TrendingUp} label="Avg progress" value={`${data?.stats?.avg_progress ?? 0}%`} />
                    <StatCard icon={FolderKanban} label="Active projects" value={data?.stats?.projects ?? 0} />
                    <div className="bg-espresso text-white p-5 grain">
                        <StageIcon className="h-5 w-5 text-brand-gold" />
                        <p className="mt-4 text-xs text-white/50 uppercase tracking-wide">Your stage</p>
                        <p className="font-display font-semibold text-lg">{stage.label}</p>
                    </div>
                </div>

                <div className="mt-6 bg-white border border-[#e8e1d4] p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display font-semibold text-lg">Your artist journey</h2>
                        <span className="text-xs text-neutral-500">Stage {stage.step} of 4</span>
                    </div>
                    <Progress value={(stage.step / 4) * 100} className="h-2 bg-cream" />
                    <div className="mt-4 grid grid-cols-4 gap-2 text-center text-[11px] uppercase tracking-wide">
                        {Object.values(STAGE_META).map((s, i) => (
                            <span key={i} className={i < stage.step ? "text-brand-golddark font-semibold" : "text-neutral-400"}>{s.label.split(" ")[0]}</span>
                        ))}
                    </div>
                </div>

                <div className="mt-10">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="font-display font-semibold text-xl">My Courses</h2>
                        <Link to="/courses" className="text-sm link-gold inline-flex items-center gap-1.5">Browse more <ArrowRight className="h-4 w-4" /></Link>
                    </div>
                    {data?.enrollments?.length ? (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {data.enrollments.map((e) => (
                                <div key={e.id} className="bg-white border border-[#e8e1d4] overflow-hidden" data-testid={`dashboard-course-${e.course_slug}`}>
                                    {e.course && <Link to={`/learn/${e.course_slug}`} className="block aspect-[16/9] relative group"><img loading="lazy" decoding="async" src={e.course.cover} alt="" className="h-full w-full object-cover" /><div className="absolute inset-0 grid place-items-center bg-espresso/30 group-hover:bg-espresso/10 transition-colors"><Play className="h-9 w-9 text-white" /></div></Link>}
                                    <div className="p-4">
                                        <h3 className="font-display font-semibold leading-tight">{e.course?.title || e.course_slug}</h3>
                                        <Progress value={e.progress} className="h-1.5 mt-3 bg-cream" />
                                        <p className="mt-2 text-xs text-neutral-500">{e.progress}% complete</p>
                                        <p className={`mt-1 text-xs ${e.access && !e.access.active ? "text-red-600" : "text-neutral-500"}`} data-testid={`dashboard-access-${e.course_slug}`}>
                                            {!e.access || e.access.lifetime ? "Lifetime access" : e.access.revoked ? "Access removed" : e.access.active ? `Access until ${new Date(e.access.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : `Access ended ${new Date(e.access.expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`}
                                        </p>
                                        <div className="mt-3 flex gap-2">
                                            {e.access && !e.access.active
                                                ? <a href="https://wa.me/919111895557?text=Hi%2C%20I%27d%20like%20to%20renew%20my%20course%20access" target="_blank" rel="noreferrer" className="btn-primary !px-4 !py-2 text-[11px] flex-1" data-testid={`dashboard-renew-${e.course_slug}`}>Renew access</a>
                                                : <Link to={`/learn/${e.course_slug}`} className="btn-dark !px-4 !py-2 text-[11px] flex-1" data-testid={`dashboard-continue-${e.course_slug}`}>{e.progress > 0 ? "Continue" : "Start"}</Link>}
                                            {e.progress === 100 && <Link to={`/certificate/${e.course_slug}`} className="btn-primary !px-3 !py-2 text-[11px]" data-testid={`dashboard-cert-${e.course_slug}`}><Award className="h-4 w-4" /></Link>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white border border-[#e8e1d4] p-10 text-center">
                            <p className="text-neutral-500">You haven't enrolled in any courses yet.</p>
                            <Link to="/courses" className="btn-primary mt-5 inline-flex" data-testid="dashboard-explore-courses">Explore Courses <ArrowRight className="h-4 w-4" /></Link>
                        </div>
                    )}
                </div>

                {(data?.blueprint?.length || data?.purchases?.length) ? (
                    <div className="mt-10">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="font-display font-semibold text-xl">My Library</h2>
                            <Link to="/resources" className="text-sm link-gold inline-flex items-center gap-1.5">Browse more <ArrowRight className="h-4 w-4" /></Link>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {data.blueprint?.map((b, i) => (
                                <div key={`bp${i}`} className="bg-white border border-[#e8e1d4] overflow-hidden" data-testid="dashboard-blueprint">
                                    <div className="p-5">
                                        <span className="text-[10px] font-bold tracking-widest text-brand-golddark">EBOOK · LIFETIME ACCESS</span>
                                        <h3 className="font-display font-semibold leading-tight mt-1.5">{b.title}</h3>
                                        <div className="mt-4 flex gap-2">
                                            <a href={`/blueprint/read?token=${b.access_token}`} className="btn-dark !px-4 !py-2 text-[11px] flex-1 inline-flex items-center justify-center gap-1.5" data-testid="dashboard-blueprint-read"><BookOpen className="h-4 w-4" /> Read</a>
                                            <a href={`${BACKEND}/api/blueprint/pdf/${b.access_token}?download=1`} className="btn-primary !px-3 !py-2 text-[11px]" data-testid="dashboard-blueprint-download"><Download className="h-4 w-4" /></a>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {data.purchases?.map((p) => (
                                <div key={p.slug} className="bg-white border border-[#e8e1d4] overflow-hidden" data-testid={`dashboard-product-${p.slug}`}>
                                    {p.cover ? <div className="aspect-[16/9] bg-cream"><img loading="lazy" src={p.cover} alt="" className="h-full w-full object-cover" /></div> : <div className="aspect-[16/9] bg-cream grid place-items-center"><Package className="h-8 w-8 text-brand-golddark" /></div>}
                                    <div className="p-5">
                                        <span className={`text-[10px] font-bold tracking-widest ${p.active ? "text-brand-golddark" : "text-red-500"}`}>{p.lifetime ? "LIFETIME ACCESS" : (p.active ? `ACCESS UNTIL ${new Date(p.expires_at).toLocaleDateString()}` : "ACCESS EXPIRED")}</span>
                                        <h3 className="font-display font-semibold leading-tight mt-1.5">{p.title}</h3>
                                        <div className="mt-4">
                                            {p.active
                                                ? (p.has_file
                                                    ? <button onClick={() => downloadProduct(p.slug, p.title)} className="btn-dark !px-4 !py-2 text-[11px] w-full inline-flex items-center justify-center gap-1.5" data-testid={`dashboard-product-download-${p.slug}`}><Download className="h-4 w-4" /> Download</button>
                                                    : <p className="text-xs text-neutral-400">File will be available soon.</p>)
                                                : <p className="text-xs text-red-500">Your access period has ended. Repurchase to regain access.</p>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}

                {data?.bhajan?.length ? (
                    <div className="mt-10">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="font-display font-semibold text-xl">My Bhajan Bookings</h2>
                            <Link to="/bhajans" className="text-sm link-gold inline-flex items-center gap-1.5">View offer <ArrowRight className="h-4 w-4" /></Link>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {data.bhajan.map((b, i) => (
                                <div key={b.order_id || i} className="bg-espresso text-white overflow-hidden grain" data-testid={`dashboard-bhajan-${i}`}>
                                    <div className="p-5">
                                        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest text-brand-gold"><Music className="h-3.5 w-3.5" /> BOOKING CONFIRMED</span>
                                        <h3 className="font-display font-semibold leading-tight mt-1.5">{b.offer_name}</h3>
                                        <p className="mt-1 text-xs text-white/50">Ref: {b.reference} · Paid ₹{Number(b.amount || 0).toLocaleString("en-IN")}{b.paid_at ? ` · ${new Date(b.paid_at).toLocaleDateString()}` : ""}</p>
                                        {b.allotted_package && <p className="mt-2 text-xs text-brand-gold">Package: {b.allotted_package}</p>}
                                        <div className="mt-4 flex gap-2">
                                            <a href={`/bhajans/thank-you?order=${b.order_id}`} className="btn-primary !px-4 !py-2 text-[11px] flex-1 inline-flex items-center justify-center gap-1.5" data-testid={`dashboard-bhajan-open-${i}`}><MessageCircle className="h-4 w-4" /> {b.details_saved ? "View / update details" : "Share project details"}</a>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}

                {data && !data.has_password ? (
                    <div className="mt-10 bg-white border border-[#e8e1d4] p-6 max-w-xl" data-testid="dashboard-set-password">
                        <div className="flex items-center gap-2 mb-1"><KeyRound className="h-5 w-5 text-brand-golddark" /><h2 className="font-display font-semibold text-lg">Set a password</h2></div>
                        {pwDone ? (
                            <p className="text-sm text-green-600 mt-2">Password set! You can now log in with your email and password.</p>
                        ) : (
                            <>
                                <p className="text-sm text-neutral-500 mt-1">Your account was created from a purchase. Set a password so you can log in directly next time (no email link needed).</p>
                                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                                    <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Choose a password (min 6 chars)" data-testid="dashboard-password-input" className="flex-1 border border-[#d9cfbc] px-4 py-2.5 text-sm outline-none focus:border-ink" />
                                    <button onClick={savePassword} disabled={pwBusy} className="btn-primary disabled:opacity-60" data-testid="dashboard-password-save">{pwBusy ? "Saving…" : "Set password"}</button>
                                </div>
                            </>
                        )}
                    </div>
                ) : null}
            </div>
            <AIChat />
        </div>
    );
}

function StatCard({ icon: Icon, label, value }) {
    return (
        <div className="bg-white border border-[#e8e1d4] p-5">
            <Icon className="h-5 w-5 text-brand-golddark" />
            <p className="mt-4 font-display font-semibold text-4xl">{value}</p>
            <p className="text-xs text-neutral-500 uppercase tracking-wide">{label}</p>
        </div>
    );
}
